import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Camera, ImagePlus, Trash2, Loader2, Image as ImageIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { compressImageForUpload } from '@/lib/imageUtils';
import { cn } from '@/lib/utils';

interface ElementoFoto {
  id: string;
  url_archivo: string;
  nombre_archivo: string;
  created_at: string;
}

interface Props {
  elementoId: string;
  elementoNombre?: string;
  size?: 'sm' | 'icon';
  className?: string;
}

const BUCKET = 'elemento-fotos';

export const ElementoFotosButton: React.FC<Props> = ({
  elementoId,
  elementoNombre,
  size = 'sm',
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [fotos, setFotos] = useState<ElementoFoto[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Cargar contador inicial siempre
  useEffect(() => {
    let cancel = false;
    (async () => {
      const { count: c } = await supabase
        .from('elemento_fotos')
        .select('id', { count: 'exact', head: true })
        .eq('elemento_id', elementoId);
      if (!cancel) setCount(c || 0);
    })();
    return () => {
      cancel = true;
    };
  }, [elementoId]);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('elemento_fotos')
        .select('*')
        .eq('elemento_id', elementoId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const list = data || [];
      setFotos(list);
      setCount(list.length);
      const map: Record<string, string> = {};
      await Promise.all(
        list.map(async (f) => {
          const { data: signed } = await supabase.storage
            .from(BUCKET)
            .createSignedUrl(f.url_archivo, 3600);
          if (signed?.signedUrl) map[f.id] = signed.signedUrl;
        }),
      );
      setUrls(map);
    } catch (e) {
      console.error(e);
      toast({ title: 'Error', description: 'No se pudieron cargar las fotos', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const subir = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      // calidad justita: dimension media y compresión alta
      const comp = await compressImageForUpload(file, { maxDimension: 900, quality: 0.55 });
      const ext = comp.name.split('.').pop() || 'webp';
      const path = `${elementoId}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, comp, { contentType: comp.type });
      if (upErr) throw upErr;

      const { data: userData } = await supabase.auth.getUser();
      const { error: insErr } = await supabase.from('elemento_fotos').insert({
        elemento_id: elementoId,
        url_archivo: path,
        nombre_archivo: file.name,
        tamano_archivo: comp.size,
        subido_por: userData.user?.id,
      });
      if (insErr) throw insErr;

      toast({ title: 'Foto añadida' });
      if (cameraRef.current) cameraRef.current.value = '';
      if (galleryRef.current) galleryRef.current.value = '';
      await cargar();
    } catch (err) {
      console.error(err);
      toast({ title: 'Error', description: 'No se pudo subir la foto', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const eliminar = async (foto: ElementoFoto) => {
    if (!confirm('¿Eliminar esta foto?')) return;
    try {
      await supabase.storage.from(BUCKET).remove([foto.url_archivo]);
      const { error } = await supabase.from('elemento_fotos').delete().eq('id', foto.id);
      if (error) throw error;
      toast({ title: 'Foto eliminada' });
      await cargar();
    } catch (e) {
      console.error(e);
      toast({ title: 'Error', description: 'No se pudo eliminar', variant: 'destructive' });
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn('h-8 w-8 p-0 relative', className)}
            title="Fotos del elemento"
            onClick={(e) => e.stopPropagation()}
          >
            <Camera className="h-4 w-4" />
            {count > 0 && (
              <Badge
                variant="secondary"
                className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 text-[10px]"
              >
                {count}
              </Badge>
            )}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Fotos {elementoNombre ? `— ${elementoNombre}` : ''}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <Button
              type="button"
              variant="outline"
              className="h-14 flex flex-col items-center justify-center gap-1"
              onClick={() => cameraRef.current?.click()}
              disabled={uploading}
            >
              <Camera className="h-5 w-5" />
              <span className="text-xs">Hacer foto</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-14 flex flex-col items-center justify-center gap-1"
              onClick={() => galleryRef.current?.click()}
              disabled={uploading}
            >
              <ImagePlus className="h-5 w-5" />
              <span className="text-xs">Galería</span>
            </Button>
          </div>
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={subir}
          />
          <input
            ref={galleryRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={subir}
          />

          {uploading && (
            <div className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Subiendo…
            </div>
          )}

          <div className="mt-2">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : fotos.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                <ImageIcon className="h-10 w-10 mx-auto mb-2 opacity-40" />
                Aún no hay fotos para este elemento.
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {fotos.map((foto) => (
                  <div
                    key={foto.id}
                    className="relative group border rounded overflow-hidden bg-muted"
                  >
                    <button
                      type="button"
                      className="block w-full aspect-square"
                      onClick={() => setPreview(urls[foto.id] || null)}
                    >
                      {urls[foto.id] ? (
                        <img
                          src={urls[foto.id]}
                          alt={foto.nombre_archivo}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      )}
                    </button>
                    <Button
                      type="button"
                      size="icon"
                      variant="destructive"
                      className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => eliminar(foto)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Vista previa</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="flex justify-center">
              <img
                src={preview}
                alt="Vista previa"
                className="max-w-full max-h-[75vh] object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
